import {
  Alert,
  AlertTitle,
  Section,
  SectionHeader,
  SectionContent,
  FormField,
  FormDescription,
  FormMessage,
  useFormContext,
} from '@predoc/cow';
import { Info } from 'lucide-react';
import { PresignedFileUpload } from '@/components';
import { FileDisplay } from '@/components/FileDisplay';
import { NestedRequestActionFormFieldProps } from './validation';
import { zipContainsOnlyPdfsRule, fileSizeRule } from '@/utils/files';
import { FormLabel } from '@predoc/cow';
import type { PresignedUrlResponse } from '@/types/file-upload';

export const Uploads = () => {
  const { control, formState } = useFormContext();

  return (
    <Section className="bg-white">
      <SectionHeader>
        <h1 className="text-xl font-bold">Medical Record Upload</h1>
      </SectionHeader>
      <SectionContent>
        <div className="flex flex-col gap-4">
          <Alert variant="accent" className="bg-muted/70 border-0">
            <Info size={16} />
            <AlertTitle className="text-sm">
              Only upload medical records that pertain to this specific patient
            </AlertTitle>
          </Alert>

          <FormField
            control={control}
            name="RECORD_UPLOAD.uploadedFiles"
            render={({
              field,
            }: NestedRequestActionFormFieldProps<'RECORD_UPLOAD.uploadedFiles'>) => {
              const uploadedFiles = field.value || [];
              const hasFile = uploadedFiles.length > 0;
              const uploadedFile = hasFile ? uploadedFiles[0] : null;

              const handleRemoveFile = () => {
                field.onChange([]);
              };

              const handleUploadSuccess = (files: PresignedUrlResponse[]) => {
                field.onChange(files);
              };

              return (
                <div className="space-y-2">
                  <FormLabel>Medical Records *</FormLabel>
                  <PresignedFileUpload
                    hasError={!!formState.errors.uploadedFiles}
                    multiple={false}
                    acceptedTypes=".zip"
                    disabled={hasFile}
                    validationRules={[
                      zipContainsOnlyPdfsRule,
                      fileSizeRule(350),
                    ]}
                    onChange={handleUploadSuccess}
                  />
                  <FormMessage />
                  <FormDescription>
                    Please upload a single ZIP file (max 350 MB) containing only
                    PDF documents.
                  </FormDescription>
                  {uploadedFile && (
                    <FileDisplay
                      file={uploadedFile}
                      onRemove={handleRemoveFile}
                    />
                  )}
                </div>
              );
            }}
          />
        </div>
      </SectionContent>
    </Section>
  );
};
